import { useState } from 'react';
import useSWR, { mutate as swrMutate } from 'swr';
import { AudioLines, Loader2, Mic, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import {
  checkMicrophone,
  deleteMicrophone,
  listZoneMicrophones,
  updateMicrophone,
  type MicrophoneCheckResult,
  type MicrophoneRecord,
} from '@/api/microphones';
import { extractApiError } from '@/utils/apiError';

interface Props {
  zoneId: number | string;
}

export const micSwrKey = (zoneId: number | string) => `zone-microphones:${zoneId}`;

/**
 * Equipment-tab Microphone section. The audio sibling of {@link NVRPanel}.
 *
 * Renders one card per mic returned by
 * `GET /equipment_discovery/zones/:zoneId/microphones/`. A microphone is
 * a USB/ALSA capture device on the zone's Jetson; its presence (when
 * `enabled`) lights up the speech-analytics tab via `Zone.has_mic`.
 * Сетевой микрофон (`kind: 'network'`, роутер у кассы) показывает адрес и
 * кнопку «Проверить звук»: несколько секунд с роутера — уровни и плеер.
 *
 * Empty-state contract mirrors NVRPanel: render `null` and let the
 * `<AddDeviceDropdown>` own the add affordance.
 */
export default function MicrophonePanel({ zoneId }: Props) {
  const swrKey = micSwrKey(zoneId);
  const { data, error, isLoading } = useSWR<
    MicrophoneRecord[],
    { response?: { status?: number } }
  >(
    swrKey,
    () => listZoneMicrophones(zoneId).then(r => r.data.results),
    { shouldRetryOnError: false },
  );

  const notFound = error?.response?.status === 404;
  const list = data ?? [];
  const exists = list.length > 0;

  const reload = () => swrMutate(swrKey);

  if (!isLoading && !exists && (notFound || !error)) return null;
  if (isLoading && !data && !error) return null;

  return (
    <>
      {list.map(mic => (
        <MicCard key={mic.id} mic={mic} onChanged={reload} />
      ))}
    </>
  );
}

function MicCard({ mic, onChanged }: { mic: MicrophoneRecord; onChanged: () => void }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<MicrophoneCheckResult | null>(null);
  const isNetwork = mic.kind === 'network';

  const runCheck = async () => {
    setChecking(true);
    setError(null);
    setCheck(null);
    try {
      const { data } = await checkMicrophone(mic.id);
      setCheck(data);
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setChecking(false);
    }
  };

  const toggleEnabled = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateMicrophone(mic.id, { enabled: !mic.enabled });
      onChanged();
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  };

  // Two-click destructive — matches the NVR/Router delete pattern.
  const onDelete = async () => {
    if (!armed) {
      setArmed(true);
      window.setTimeout(() => setArmed(false), 5000);
      return;
    }
    setBusy(true);
    try {
      await deleteMicrophone(mic.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <h2 className="mb-1 flex items-center gap-2 text-base font-semibold">
          <Mic className="size-4" />
          {isNetwork ? t('equipment.mic.networkTitle') : t('equipment.mic.title')}
          {!mic.enabled && (
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
              {t('equipment.mic.disabled')}
            </span>
          )}
        </h2>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <Field
            label={isNetwork ? t('equipment.mic.address') : t('equipment.mic.alsaDevice')}
            value={mic.alsa_device || '—'}
            mono
          />
          {mic.card_name && (
            <Field label={t('equipment.mic.cardName')} value={mic.card_name} />
          )}
          <Field
            label={t('equipment.mic.capture')}
            value={`${mic.sample_rate} Hz · ${mic.channels === 1 ? 'mono' : `${mic.channels}ch`}`}
            mono
          />
        </div>

        {mic.notes && (
          <div className="whitespace-pre-wrap border-t pt-2 text-sm text-muted-foreground">
            {mic.notes}
          </div>
        )}

        {isNetwork && (
          <div className="space-y-2 border-t pt-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={runCheck} disabled={checking}>
                {checking ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <AudioLines className="size-3.5" />
                )}
                {checking ? t('equipment.mic.checking') : t('equipment.mic.check')}
              </Button>
              {check && check.online !== null && (
                <Badge variant={check.online ? 'success' : 'destructive'}>
                  {check.online ? t('equipment.mic.online') : t('equipment.mic.offline')}
                </Badge>
              )}
              {check?.online && check.rms_db != null && check.peak_db != null && (
                <span className="text-sm tabular-nums text-muted-foreground">
                  {t('equipment.mic.levels', { rms: formatDb(check.rms_db), peak: formatDb(check.peak_db) })}
                </span>
              )}
            </div>
            {check?.error && (
              <div className="text-xs text-muted-foreground">{check.error}</div>
            )}
            {check?.audio_b64 && (
              <audio
                controls
                className="h-9 w-full"
                src={`data:${check.audio_mime ?? 'audio/mpeg'};base64,${check.audio_b64}`}
              />
            )}
          </div>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t pt-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={mic.enabled}
              onChange={toggleEnabled}
              disabled={busy}
              className="cursor-pointer"
            />
            {t('equipment.mic.enabled')}
          </label>
          <Button variant="destructive" size="sm" onClick={onDelete} disabled={busy}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
            {armed ? t('equipment.mic.deleteConfirm') : t('equipment.mic.delete')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** −29 дБ: целые, с типографским минусом. */
function formatDb(db: number) {
  return `${Math.round(db)}`.replace('-', '−');
}

function Field({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`break-all text-sm${mono ? ' font-mono' : ''}`}>{value}</div>
    </div>
  );
}
